import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedList, FormattedMessage, useIntl } from 'react-intl';
import { AutoCalibrationRule } from 'utils/models/Calibration';
import {
  CalibrationRuleFormValues,
  ConditionTypeDefinition,
  createDefaultRule,
  formValuesToRule,
  getConditionTypeDefinitions,
  getPeriodTypeOptions,
  getRatingTargetOptions,
  getSupportedPlaceholders,
  ruleToFormValues,
  validateRuleFormValues,
} from 'utils/models/CalibrationRuleConditions';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import AutoCalibrationRulePreview from './AutoCalibrationRulePreview';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  rule: AutoCalibrationRule | null;
  onSave: (rule: AutoCalibrationRule) => void;
}

const AutoCalibrationRuleEditorModal: FC<Props> = ({
  isOpen,
  toggle,
  rule,
  onSave,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;

  const definitions = useMemo(() => getConditionTypeDefinitions(intl), [intl]);

  const initialValues = useMemo(
    () => ruleToFormValues(rule ?? createDefaultRule(definitions[0])),
    [rule, definitions]
  );

  const [values, setValues] =
    useState<CalibrationRuleFormValues>(initialValues);

  // Reset the form each time the modal opens for a (possibly different) rule.
  useEffect(() => {
    if (isOpen) {
      setValues(initialValues);
    }
  }, [isOpen, initialValues]);

  const definition: ConditionTypeDefinition | undefined = useMemo(
    () => definitions.find((d) => d.type === values.condition_type),
    [definitions, values.condition_type]
  );

  const handleChange = useCallback(
    (next: CalibrationRuleFormValues) => {
      if (next.condition_type !== values.condition_type) {
        // Switching condition type swaps the condition-specific fields, the
        // allowed look-back periods and the default message, since the
        // supported placeholders differ per type.
        const nextDefinition = definitions.find(
          (d) => d.type === next.condition_type
        );
        if (nextDefinition) {
          setValues({
            ...ruleToFormValues(createDefaultRule(nextDefinition)),
            enabled: next.enabled,
          });
          return;
        }
      }
      setValues(next);
    },
    [definitions, values.condition_type]
  );

  const previewRule = useMemo((): AutoCalibrationRule | null => {
    if (!definition) {
      return null;
    }
    const errors = validateRuleFormValues(values, definition, intl);
    return Object.keys(errors).length === 0
      ? formValuesToRule(values, definition)
      : null;
  }, [values, definition, intl]);

  const inputs = useMemo(() => {
    const supportedPlaceholders = getSupportedPlaceholders(definition);
    const targetValue = values.target;
    const targetOptions = [
      ...getRatingTargetOptions(intl),
      // Rules edited outside this UI may target specific rating values;
      // keep that selectable rather than silently rewriting it.
      ...(Array.isArray(targetValue)
        ? [
            {
              id: targetValue,
              name: formatMessage(
                {
                  id: 'app.views.administration.campaign_flow.auto_calibration_rules.editor.target.specific_ratings',
                  defaultMessage: 'Ratings {ratings}',
                },
                {
                  ratings: intl.formatList(targetValue.map(String), {
                    type: 'disjunction',
                    style: 'long',
                  }),
                }
              ),
            },
          ]
        : []),
    ];

    const conditionInputs = (definition?.fields ?? []).map((field) => {
      const common = {
        name: field.name,
        label: field.label,
        required: true,
      };
      if (field.kind === 'number') {
        return {
          ...common,
          type: INPUT_TYPES.TEXT,
          inputType: 'number',
          minValue: field.minValue,
        };
      }
      if (field.kind === 'multiselect') {
        return {
          ...common,
          required: false,
          type: INPUT_TYPES.CHECKBOX_MULTI,
          options: (field.options ?? []).map((o) => ({
            key: o.id,
            label: o.name,
          })),
        };
      }
      return {
        ...common,
        type: INPUT_TYPES.DROPDOWN,
        objects: field.name === 'target' ? targetOptions : field.options,
      };
    });

    const periodTypes = definition?.periodTypes ?? [];

    return [
      {
        name: 'template',
        type: INPUT_TYPES.TEXTAREA,
        required: true,
        label: formatMessage({
          id: 'app.views.administration.campaign_flow.auto_calibration_rules.editor.template.label',
          defaultMessage: 'Flag message',
        }),
        helperText: (
          <>
            <FormattedMessage
              id="app.views.administration.campaign_flow.auto_calibration_rules.editor.template.helper_text"
              defaultMessage="Supported placeholders:"
            />{' '}
            <FormattedList
              type="conjunction"
              style="long"
              value={supportedPlaceholders.map((placeholder) => (
                <code key={placeholder}>{`{${placeholder}}`}</code>
              ))}
            />
          </>
        ),
      },
      {
        name: 'condition_type',
        type: INPUT_TYPES.DROPDOWN,
        required: true,
        label: formatMessage({
          id: 'app.views.administration.campaign_flow.auto_calibration_rules.editor.condition_type.label',
          defaultMessage: 'Condition type',
        }),
        helperText: definition?.description,
        objects: definitions.map((d) => ({ id: d.type, name: d.label })),
      },
      ...conditionInputs,
      {
        name: 'period_type',
        type: INPUT_TYPES.DROPDOWN,
        required: true,
        disabled: periodTypes.length <= 1,
        label: formatMessage({
          id: 'app.views.administration.campaign_flow.auto_calibration_rules.editor.period_type.label',
          defaultMessage: 'Look-back period',
        }),
        objects: getPeriodTypeOptions(intl, periodTypes),
      },
      {
        name: 'period_duration',
        type: INPUT_TYPES.TEXT,
        inputType: 'number',
        minValue: definition?.minimumDuration ?? 1,
        required: true,
        label: formatMessage({
          id: 'app.views.administration.campaign_flow.auto_calibration_rules.editor.period_duration.label',
          defaultMessage: 'Look-back length',
        }),
        helperText: formatMessage(
          {
            id: 'app.views.administration.campaign_flow.auto_calibration_rules.editor.period_duration.helper_text',
            defaultMessage: 'Minimum {minimum} for this condition.',
          },
          { minimum: definition?.minimumDuration ?? 1 }
        ),
      },
      {
        name: 'preview',
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        component: AutoCalibrationRulePreview,
        rule: previewRule,
      },
      {
        name: 'footnote',
        type: INPUT_TYPES.CUSTOM_INPUT,
        component: (
          <p className="alert alert-light border mb-0">
            <FormattedMessage
              id="app.views.administration.campaign_flow.auto_calibration_rules.editor.footnote"
              defaultMessage="Each matching rule adds a flag. Flags never change a person's rating."
            />
          </p>
        ),
      },
    ];
  }, [definition, definitions, formatMessage, previewRule, values]);

  const onValidate = useCallback(
    (obj: CalibrationRuleFormValues) =>
      validateRuleFormValues(obj, definition, intl),
    [definition, intl]
  );

  // ConfirmForm calls this with the form object and a done callback
  // (data, error, hardErrorMessage) instead of awaiting a promise.
  const submitPromise = useCallback(
    (
      obj: CalibrationRuleFormValues,
      done: (
        data: AutoCalibrationRule | null,
        error?: unknown,
        hardErrorMessage?: string | null
      ) => void
    ) => {
      if (!definition) {
        done(
          null,
          null,
          formatMessage({
            id: 'app.views.administration.campaign_flow.auto_calibration_rules.editor.error.unknown_condition_type',
            defaultMessage: 'Select a condition type before saving.',
          })
        );
        return;
      }
      done(formValuesToRule(obj, definition));
    },
    [definition, intl]
  );

  const callback = useCallback(
    (data?: AutoCalibrationRule) => {
      if (data) {
        onSave(data);
      }
    },
    [onSave]
  );

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      title={
        rule
          ? formatMessage({
              id: 'app.views.administration.campaign_flow.auto_calibration_rules.editor.title.edit',
              defaultMessage: 'Edit automatic calibration flag',
            })
          : formatMessage({
              id: 'app.views.administration.campaign_flow.auto_calibration_rules.editor.title.add',
              defaultMessage: 'Add automatic calibration flag',
            })
      }
      submitText={formatMessage({
        id: 'app.views.administration.campaign_flow.auto_calibration_rules.editor.submit',
        defaultMessage: 'Save rule',
      })}
      object={values}
      onChange={handleChange}
      inputs={inputs}
      onValidate={onValidate}
      submitPromise={submitPromise}
      callback={callback}
      // apply the saved rule to the list right away rather than after the
      // close animation, so the row updates while the modal fades out
      triggerCallbackBeforeClose={true}
      disableUnsavedChangesPrompt={true}
    />
  );
};

export default React.memo(AutoCalibrationRuleEditorModal);
